import classnames from 'classnames';
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  FunctionComponent,
  ReactNode
} from 'react';
import { CSSTransition } from 'react-transition-group';

import { BFLoader } from '@components/common/loader/main';
import {
  ActionsOpenDirections,
  BaseDropdownProps,
  DropdownVariants,
  LabelPosition,
  ListOpenDirections,
  OpenDirectionOverride,
} from '@components/library/dropdown';
import { getListDropdownOptionDomId } from '@components/library/dropdown/dropdown_helper';
import { isOutOfView } from '@helpers/isInView';
import { manageDropdownListener, manageKeydownListener } from '@helpers/use_effect_callbacks';

import '@components/library/dropdown/dropdown_transitions.scss';

const baseName = 'dropdown';

// Module-level counter to ensure unique IDs across all component instances
let instanceCounter = 0;

const BaseDropdown: FunctionComponent<BaseDropdownProps> = (props) => {
  const {
    anchorElement,
    'aria-label': ariaLabelFromDom,
    ariaLabel: ariaLabelCamelCase,
    anchorElementLabel,
    'aria-labelledby': ariaLabelledBy,
    arrowedItem,
    children,
    className = '',
    disableDynamicPositioning,
    disabled,
    styleClassNames,
    variantName,
    dropdownTransition = '',
    dropdownTransitionTime = 300,
    closeCallback = undefined,
    error,
    expandOnSpacebarKeypress = true,
    keydownCallback,
    id,
    isLoading,
    isNestedDropdown,
    label,
    labelPosition = LabelPosition.Top,
    listItemIdPrefix,
    selectedItemId,
    openDirection = ActionsOpenDirections.DownLeft,
    openOnClick = true,
    openOnHover = false,
    overflowParentRef,
    required,
    setIsDropdownOpen = (): void => { /* noop */ },
    toggleDropdown = false,
    tooltip,
    useUlContainer = true,
    userDevice = BFG?.currentUser?.device || 'desktop-view',
    virtualizeOptions,
  } = props;
  const ariaLabel = ariaLabelFromDom ?? ariaLabelCamelCase;
  const isMounted = useRef(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openDirectionOverride, setOpenDirectionOverride] = useState<OpenDirectionOverride | null>(null);
  const [inlineStyles, setInlineStyles] = useState({});
  const [storedTimeouts, setStoredTimeouts] = useState<number[]>([]);

  const anchorAccessibleName = ariaLabel || anchorElementLabel;

  const menuRef = useRef<HTMLDivElement | null>(null);
  const contentContainerDivRef = useRef<HTMLDivElement | null>(null);
  const contentContainerUlRef = useRef<HTMLUListElement | null>(null);

  // Generate a unique instance ID once per component instance
  const instanceIdRef = useRef<string | null>(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = `dropdown-instance-${++instanceCounter}-${Date.now()}`;
  }

  const cancelStoredTimeouts = (): void => {
    storedTimeouts.forEach((storedTimeout) => clearTimeout(storedTimeout));
  };

  const isTouchScreen = userDevice === 'mobile-view' || userDevice === 'tablet-view';

  const handleOpenAndClose = (e: any): void => {
    const triggerKeys = expandOnSpacebarKeypress
      ? ['ArrowUp', 'ArrowDown', 'Enter', 'Spacebar', ' ']
      : ['ArrowUp', 'ArrowDown', 'Enter'];
    const updateShowDropdown = (): void => {
      setShowDropdown((prevState) => {
        // if update is to open the dropdown, close other BF dropdowns that may be open
        // in most cases, other dropdowns will close due to clicking away from them,
        // which is handled in the 'manageDropdownListener' method, however,
        // this 'closeBFDropdowns' event is necessary because code may contain e.stopPropagation()
        // which prevents 'manageDropdownListener' from detecting the event
        if (!prevState && !isNestedDropdown) window.BF?.fx?.dispatchWindowEvent('closeBFDropdowns'); // eslint-disable-line no-unused-expressions
        return !prevState;
      });
    };

    switch (e.type) {
      case 'keydown':
        // toggle the menu open/closed, but if a dropdown item is targeted (highlighted via arrow
        // keys) defer to manageKeydownListener method so that 'Enter' keydown will select it
        // !arrowedItem is needed because the keydown event here was intercepting the 'enter'
        // intended to select a arrowedItem
        if (!showDropdown && (triggerKeys.includes(e.key))) updateShowDropdown();
        break;
      case 'click':
        if (openOnClick || isTouchScreen) updateShowDropdown();
        break;
      case 'mouseenter':
      case 'mouseleave':
        if (openOnHover && !isTouchScreen) setShowDropdown(e.type === 'mouseenter');
        break;
      default:
    }
  };

  const runDynamicPositioning = (): void => {
    cancelStoredTimeouts();
    const { outOfViewport, outOfOverflowParent } = isOutOfView(contentContainerDivRef?.current || contentContainerUlRef?.current, overflowParentRef?.current);
    if (showDropdown && (outOfViewport.bottom || outOfOverflowParent.bottom)) { // overflows bottom of viewport or parent container
      setOpenDirectionOverride(OpenDirectionOverride.Up);

      // if an overflowParentRef is provided, check if the dropdown will overflow the parent container when it's respositioned to open up
      if (overflowParentRef?.current && menuRef?.current) {
        const containerOffset = 15; // gap between dropdown and overflow container
        const dropdownWrapperCoords = menuRef.current.getBoundingClientRect();
        const gapBetweenDropdownWrapperAndContainer = dropdownWrapperCoords.top - outOfOverflowParent.bounding.top;
        const dropdownHeight = outOfViewport.bounding.height;
        const willBeOutOfContainerParentTop = gapBetweenDropdownWrapperAndContainer > 0 && dropdownHeight > (gapBetweenDropdownWrapperAndContainer + containerOffset);

        if (willBeOutOfContainerParentTop || outOfOverflowParent?.top) {
          const bottom = `${dropdownWrapperCoords.bottom - (outOfOverflowParent.bounding.top + containerOffset + dropdownHeight)}px`;
          setInlineStyles({ bottom });
        }
      }
    }

    // reset state for dynamic dropdown placement (refs need to be reset so initially rendered dropdown will be out of view)
    if (!showDropdown) {
      const newTimeout = window.setTimeout(() => {
        if (isMounted.current) {
          setOpenDirectionOverride(null);
          setInlineStyles({});
        }
      }, dropdownTransitionTime + 50); // setTimeout accounts for CSSTransition 'timeout' time, the setTimeout callback should fire after the CSSTransition transition has finished
      setStoredTimeouts((timeouts) => ([...timeouts, newTimeout]));
    }
  };

  // Generate unique IDs by combining the provided id (if any) with the unique instance ID
  // This prevents collisions when multiple instances share the same id prop
  const uniqueId = id ? `${id}-${instanceIdRef.current}` : instanceIdRef.current;
  const comboboxId = `${uniqueId}_combobox`; // ID for the anchor element (combobox)
  const labelId = `${uniqueId}_label`;
  const listboxId = `${uniqueId}_listbox`;

  const isActionsDropdown = variantName === DropdownVariants.Actions;
  const popupRole = isActionsDropdown ? 'menu' : 'listbox';

  const activeDescendantId = !isActionsDropdown && showDropdown
    ? (arrowedItem !== undefined
      ? (listItemIdPrefix
        ? getListDropdownOptionDomId(listItemIdPrefix, arrowedItem)
        : String(arrowedItem))
      : selectedItemId)
    : undefined;

  const labelHTML = (): ReactNode => (
    <label
      className={classnames(
        styleClassNames[`${variantName}--label`],
        `${variantName}--label`,
      )}
      htmlFor={comboboxId}
      id={labelId}
    >
      {label}
      {required && <span className={styleClassNames['required-asterix']}>*</span>}
      {tooltip && <span className={styleClassNames.tooltip}>{tooltip}</span>}
    </label>
  );

  const errorHTML = (): ReactNode => (
    <span
      className={`${styleClassNames['dropdown-error-container']} dropdown-error-container`}
      id={`${uniqueId}_error`}
      role="alert"
    >
      {error}
    </span>
  );

  const contentContainerClassNames: string = classnames(
    'content-container',
    'dropdown-transitions',
    styleClassNames['content-container'],
    styleClassNames[openDirectionOverride || openDirection], // orients dropdown container relative to wrapper
    {
      'virtualize-options': virtualizeOptions,
      [styleClassNames['virtualize-options']]: virtualizeOptions,
    },
  );

  const dropdownEventListenerCallback = (e: MouseEvent | KeyboardEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    setShowDropdown(false);
  };

  const cleanup = (): void => {
    setShowDropdown(false); // close dropdown when unmounting
    cancelStoredTimeouts(); // cancel all outstanding timeouts when unmounting
    isMounted.current = false;
  };

  useEffect(() => cleanup, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => (
    manageDropdownListener(menuRef, showDropdown, dropdownEventListenerCallback)
  ), [menuRef, showDropdown, dropdownEventListenerCallback]);

  useEffect(() => (
    manageKeydownListener(showDropdown, keydownCallback)
  ), [showDropdown, keydownCallback]);

  useEffect(() => {
    if (toggleDropdown) {
      setShowDropdown(() => {
        if (showDropdown && closeCallback) {
          closeCallback();
        }
        return !showDropdown;
      });
    }
  }, [toggleDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsDropdownOpen(showDropdown);
  }, [showDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (disabled) {
      setShowDropdown(false);
    }
  }, [disabled]);

  // Prevent wheel events from bubbling to scrollable ancestors (e.g. asset modal edit pane)
  // while a dropdown is open. Ancestors with overflow-y: visible still chain scroll to parents;
  // stopping propagation here keeps scrolling inside the dropdown options list.
  useEffect(() => {
    const menu = menuRef.current;
    if (!showDropdown || !menu) {
      return undefined;
    }

    const handleWheel = (e: WheelEvent): void => {
      e.stopPropagation();
    };

    menu.addEventListener('wheel', handleWheel, { passive: true });
    return (): void => {
      menu.removeEventListener('wheel', handleWheel);
    };
  }, [showDropdown]);

  useLayoutEffect(() => { // useLayoutEffect runs after render but before useEffect
    if (!disableDynamicPositioning) {
      if ((contentContainerDivRef.current && openDirection === ListOpenDirections.Down) || (contentContainerUlRef.current && openDirection === ListOpenDirections.Down)) { // TODO: setup for openDirection values other than 'down'
        runDynamicPositioning();
      }
    }
  }, [showDropdown, overflowParentRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div
        ref={menuRef}
        aria-required={required}
        className={classnames(
          className, // user specified class
          `bf-${baseName}__${variantName}`, // base-variant class for post consumption styling
          styleClassNames[variantName], // class for css modules
          {
            [styleClassNames.open]: showDropdown,
            [styleClassNames['error-and-no-label']]: !label && !!error,
            [styleClassNames['no-error-and-no-label']]: !label && !error,
            [styleClassNames['label-left']]: labelPosition === LabelPosition.Left,
            [styleClassNames.disabled]: disabled,
            open: showDropdown,
            closed: !showDropdown
          },
        )}
        id={uniqueId}
        onMouseEnter={!disabled ? handleOpenAndClose : undefined}
        onMouseLeave={!disabled ? handleOpenAndClose : undefined}
      >
        {labelPosition === LabelPosition.Top && (!!error || !!label) && (
          <div className={`${styleClassNames['top-container']} top-container`}>
            {!!label && labelHTML()}
            {!!error && errorHTML()}
          </div>
        )}
        {labelPosition === LabelPosition.Left && !!label && labelHTML()}
        {isActionsDropdown ? (
          <button
            type="button"
            aria-controls={listboxId}
            aria-expanded={showDropdown}
            aria-haspopup="menu"
            aria-label={anchorAccessibleName || (!anchorElement && !label && !ariaLabelledBy ? 'options' : undefined)}
            className={classnames(
              'anchor-element-click-container',
              styleClassNames['anchor-element-click-container'],
              { [styleClassNames.disabled]: disabled },
            )}
            data-testid="anchor-element-click-container-id"
            disabled={disabled}
            id={comboboxId}
            onClick={!disabled ? handleOpenAndClose : undefined}
            onKeyDown={!disabled ? handleOpenAndClose : undefined}
          >
            {anchorElement || 'ANCHOR ELEMEMT PLACEHOLDER'}
          </button>
        ) : (
          <div
            aria-activedescendant={activeDescendantId}
            aria-autocomplete="none"
            aria-controls={listboxId}
            aria-disabled={disabled || undefined}
            aria-expanded={showDropdown}
            aria-label={anchorAccessibleName || (!anchorElement && !label && !ariaLabelledBy ? 'options' : undefined)}
            aria-labelledby={!anchorAccessibleName ? (ariaLabelledBy || (label ? labelId : undefined)) : undefined}
            className={classnames(
              'anchor-element-click-container',
              styleClassNames['anchor-element-click-container'],
              { [styleClassNames.disabled]: disabled },
            )}
            data-testid="anchor-element-click-container-id"
            id={comboboxId}
            onClick={!disabled ? handleOpenAndClose : undefined}
            onKeyDown={!disabled ? handleOpenAndClose : undefined}
            role="combobox"
            tabIndex={!disabled ? 0 : -1}
          >
            {anchorElement || 'ANCHOR ELEMEMT PLACEHOLDER'}
          </div>
        )}
        <CSSTransition
          classNames={dropdownTransition}
          in={showDropdown}
          timeout={dropdownTransitionTime}
          unmountOnExit
        >
          {useUlContainer && !virtualizeOptions ? (
            <ul
              ref={contentContainerUlRef}
              aria-multiselectable={!isActionsDropdown && variantName === DropdownVariants.Multiselect ? true : undefined}
              className={contentContainerClassNames}
              id={listboxId}
              role={popupRole}
              style={inlineStyles}
            >
              {isLoading ? <BFLoader className={styleClassNames.loader} /> : children}
            </ul>
          ) : (
            <div
              ref={contentContainerDivRef}
              {...(useUlContainer && virtualizeOptions && {
                'aria-multiselectable': !isActionsDropdown && variantName === DropdownVariants.Multiselect ? true : undefined,
                role: popupRole,
              })}
              className={contentContainerClassNames}
              id={listboxId}
              role={popupRole}
              style={inlineStyles}
            >
              {isLoading ? <BFLoader /> : children}
            </div>
          )}
        </CSSTransition>
      </div>
      <CSSTransition
        appear
        in={labelPosition === LabelPosition.Left && !!error}
        timeout={500}
        unmountOnExit
      >
        {errorHTML()}
      </CSSTransition>
    </>
  );
};

export default BaseDropdown;
